import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, Form, Field } from 'formik';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import Grid from 'apollo-react/components/Grid';
import { roleInfoSchema } from '../../../../validation/profileValidation';
import { renderInputComponent } from '../../Form/renderInputComponent';
import ButtonCommon from '../../Button';
import PrivilegesForm from '../../../Admin/Roles/form';
import EditablePrivilegesSection from '../EditablePrivilegesSection';
import ReadOnlyPrivilegesView from '../ReadOnlyPrivilegesView/ReadOnlyPrivilegesView';
import ProfileFieldDisplay from './ProfileFieldDisplay';
import ProfileEditActions from './ProfileEditActions';
import styles from '../Profile.module.css';

function RolesFormikDirtySync({ dirty, editing, onRolesFieldsDirtyChange }) {
  useEffect(() => {
    onRolesFieldsDirtyChange?.(Boolean(editing && dirty));
  }, [dirty, editing, onRolesFieldsDirtyChange]);
  return null;
}

const RolesSection = ({
  userData,
  form,
  setForm,
  onSaveProfile,
  showPrivilegesEditor,
  onSaveRoleAndPrivileges,
  roleOptions,
  categoryOptions,
  isInternal,
  privilegesUserId,
  roleId,
  roleFields,
  userPrivileges,
  hasCustomPrivileges,
  onDirtyChange,
  privilegesSaveRef,
  editing,
  onStartEdit,
  onStopEdit,
  onDiscard,
  onRolesFieldsDirtyChange,
  canEdit,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (showPrivilegesEditor && onSaveRoleAndPrivileges) {
      onRolesFieldsDirtyChange?.(false);
    }
  }, [showPrivilegesEditor, onSaveRoleAndPrivileges, onRolesFieldsDirtyChange]);

  useEffect(() => {
    if (!editing) {
      onRolesFieldsDirtyChange?.(false);
    }
  }, [editing, onRolesFieldsDirtyChange]);

  if (showPrivilegesEditor && onSaveRoleAndPrivileges) {
    return (
      <EditablePrivilegesSection
        userId={userData.id}
        privilegesUserId={privilegesUserId}
        initialRoleId={userData.Role?.id || form.role}
        initialCategoryId={form.category}
        roleOptions={roleOptions}
        categoryOptions={categoryOptions}
        onSave={onSaveRoleAndPrivileges}
        isInternal={isInternal}
        onDirtyChange={onDirtyChange}
        saveRef={privilegesSaveRef}
        categoryName={form.categoryName}
        editing={editing}
        onStartEdit={onStartEdit}
        onStopEdit={onStopEdit}
        onDiscard={onDiscard}
        canEdit={canEdit}
      />
    );
  }

  if (userPrivileges) {
    return (
      <ReadOnlyPrivilegesView
        roleName={userData.Role?.name}
        rawPrivileges={userPrivileges}
        hasCustomPrivileges={hasCustomPrivileges}
      />
    );
  }

  if (roleId && !roleFields) {
    return (
      <>
        <div className={styles.divider} />
        <ProfileFieldDisplay
          label={t('Role')}
          value={userData.Role?.name || ''}
        />
        <PrivilegesForm caseProp="role" roleId={roleId} privilegesOnly={true} />
      </>
    );
  }

  if (roleFields && roleFields.length > 0) {
    if (!editing) {
      return (
        <>
          <div className={styles.divider} />
          {roleFields.map((field) => {
            let value = '';
            if (field.name === 'category') {
              value = form.categoryName || form.category || '';
            } else if (field.name === 'role') {
              value = userData.Role?.name || '';
            } else {
              value = form[field.name] || '';
            }

            return (
              <ProfileFieldDisplay
                key={field.name}
                label={field.label || t(field.name)}
                value={value}
              />
            );
          })}

          {form.role && (
            <>
              <div className={styles.divider} />
              <PrivilegesForm
                caseProp="role"
                roleId={form.role}
                privilegesOnly={true}
              />
            </>
          )}

          {canEdit && (
            <div className={styles.actionsRight}>
              <ButtonCommon
                color="primary"
                className={styles.btnSized}
                onClick={onStartEdit}
                text={t('edit')}
              />
            </div>
          )}
        </>
      );
    }

    return (
      <Formik
        initialValues={{
          category: form.category || '',
          categoryName: form.categoryName || '',
          role: form.role || '',
        }}
        enableReinitialize
        validationSchema={toFormikValidationSchema(roleInfoSchema(t))}
        onSubmit={async () => {
          const success = await onSaveProfile('full');
          if (success) onStopEdit();
        }}
      >
        {({ setFieldValue, handleSubmit, dirty }) => (
          <Form>
            <RolesFormikDirtySync
              dirty={dirty}
              editing={editing}
              onRolesFieldsDirtyChange={onRolesFieldsDirtyChange}
            />
            <div className={styles.divider} />
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                {roleFields.map((field) => (
                  <Field key={field.name} name={field.name}>
                    {({ field: formikField, meta }) =>
                      renderInputComponent(
                        field,
                        {
                          category: form.category || '',
                          categoryName: form.categoryName || '',
                          role: form.role || '',
                        },
                        formikField,
                        meta,
                        (value) => {
                          setFieldValue(formikField.name, value);
                          setForm({ ...form, [field.name]: value });
                        }
                      )
                    }
                  </Field>
                ))}
              </Grid>
            </Grid>

            <div className={styles.divider} />
            {form.role && (
              <PrivilegesForm
                caseProp="role"
                roleId={form.role}
                privilegesOnly={true}
              />
            )}

            <ProfileEditActions onDiscard={onDiscard} onSave={handleSubmit} />
          </Form>
        )}
      </Formik>
    );
  }

  return (
    <>
      <div className={styles.divider} />
      <ProfileFieldDisplay
        label={t('externalUsers.category')}
        value={
          userData.category?.name || userData.end_users_category?.name || ''
        }
      />
      <ProfileFieldDisplay
        label={t('profileData.organization')}
        value={userData.organizationName || ''}
      />
      <ProfileFieldDisplay
        label={t('profileData.department')}
        value={userData.departmentName || ''}
      />
      <ProfileFieldDisplay
        label={t('Role')}
        value={userData.Role?.name || t('profileData.noRoleAssigned')}
      />
      {roleId && (
        <PrivilegesForm caseProp="role" roleId={roleId} privilegesOnly={true} />
      )}
    </>
  );
};

export default RolesSection;
